// src/Pages/backup/BackupPage.tsx
import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Table,
  Typography,
  Upload,
  Segmented,
  Modal,
  Space,
  Switch,
  Tag,
} from "antd";
import {
  DownloadOutlined,
  UploadOutlined,
  InboxOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import { useBackupLogic } from "./useBackupLogic";
import BackupAyuda from "./BackupAyuda";
import { useClipboardPaste } from "../../Components/DragDropUpload";
import {
  NOTA_CON_MEDIOS,
  NOTA_SIN_MEDIOS,
  textoDesgloseRestaurado,
  textoPesoMedios,
  textoRolesCambiados,
  textoSuperadministradores,
} from "./backupUtils";

const { Text } = Typography;
const { Dragger } = Upload;

const BackupPage: React.FC = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const {
    tables,
    selectedTables,
    setSelectedTables,
    loadingTables,
    exporting,
    uploading,
    uploadMode,
    setUploadMode,
    uploadFiles,
    setUploadFiles,
    summary,
    setSummary,
    extras,
    incluirMedios,
    setIncluirMedios,
    estimacion,
    estadoEstimacion,
    exportAll,
    exportSelected,
    exportSingle,
    handleUpload,
  } = useBackupLogic();

  const [restoreModalVisible, setRestoreModalVisible] = useState(false);

  // Convención del proyecto (storage-y-visor.md §6): toda zona de upload
  // soporta paste. Acá admin puede pegar un .csv o .zip copiado del
  // explorador para encolarlo a la lista de archivos a restaurar.
  useClipboardPaste({
    active: true,
    onFile: (file) => setUploadFiles((prev) => [...prev, file]),
    accept: ".csv,.zip,application/zip,text/csv",
  });

  const tableColumns = [
    {
      title: "Tabla",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <Space>
          <DatabaseOutlined />
          <Text>{name}</Text>
        </Space>
      ),
    },
    {
      title: "Acción",
      key: "action",
      width: 120,
      render: (_: any, record: { name: string }) => (
        <Button
          size="small"
          icon={<DownloadOutlined />}
          onClick={() => exportSingle(record.name)}
        >
          CSV
        </Button>
      ),
    },
  ];

  const tableData = tables.map((t) => ({ key: t, name: t }));

  const rowSelection = {
    selectedRowKeys: selectedTables,
    onChange: (keys: React.Key[]) => setSelectedTables(keys as string[]),
  };

  const onUploadSubmit = () => {
    if (uploadMode === "restore") {
      setRestoreModalVisible(true);
    } else {
      handleUpload();
    }
  };

  const confirmRestore = () => {
    setRestoreModalVisible(false);
    handleUpload();
  };

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="historial-page"
    >
      <main style={{ padding: 24, maxWidth: 1200 }}>
        {/* La explicación completa vive en el (i) de BackupAyuda (abre un modal): el
            tooltip de PageHeader depende del hover y no cabe tanta explicación. */}
        <PageHeader
          title="Backup BD"
          subtitle="Descarga y restaura respaldos de la base de datos."
          extra={<BackupAyuda />}
        />

        {/* Download Section */}
        <Card
          title="Descargar"
          style={{ marginBottom: 24 }}
          extra={
            <Space>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={exporting}
                onClick={exportAll}
              >
                Descargar todo (ZIP)
              </Button>
              {selectedTables.length > 0 && (
                <Button
                  icon={<DownloadOutlined />}
                  loading={exporting}
                  onClick={exportSelected}
                >
                  Descargar seleccionadas ({selectedTables.length})
                </Button>
              )}
            </Space>
          }
        >
          {/* Backup con o sin medios: el ZIP completo puede pesar decenas de GB
              por los videos, así que incluirlos es una elección. El peso sale de
              la estimación del servidor. */}
          <div
            data-testid="backup-medios"
            style={{
              marginBottom: 16,
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
            }}
          >
            <Switch
              id="backup-incluir-medios"
              checked={incluirMedios}
              onChange={(v) => setIncluirMedios(v)}
              disabled={exporting}
              aria-label="Incluir fotos, videos y documentos"
              style={{ marginTop: 3 }}
            />
            <div>
              <label htmlFor="backup-incluir-medios">
                <Text strong>Incluir fotos, videos y documentos</Text>
              </label>
              <div>
                <Text data-testid="backup-medios-peso">
                  {textoPesoMedios(estimacion, estadoEstimacion)}
                </Text>
              </div>
              <Text type="secondary" data-testid="backup-medios-nota">
                {incluirMedios ? NOTA_CON_MEDIOS : NOTA_SIN_MEDIOS}
              </Text>
            </div>
          </div>
          <Table
            rowSelection={rowSelection}
            columns={tableColumns}
            dataSource={tableData}
            loading={loadingTables}
            size="small"
            pagination={false}
            scroll={{ y: 400 }}
          />
        </Card>

        {/* Upload Section */}
        <Card title="Cargar">
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <div>
              <Text strong style={{ marginRight: 12 }}>
                Modo:
              </Text>
              <Segmented
                options={[
                  { label: "Agregar/Editar", value: "incremental" },
                  { label: "Restauración completa", value: "restore" },
                ]}
                value={uploadMode}
                onChange={(v) => setUploadMode(v as "incremental" | "restore")}
              />
              {uploadMode === "restore" && (
                <Tag color="red" style={{ marginLeft: 12 }}>
                  Reemplaza datos existentes
                </Tag>
              )}
            </div>

            <Dragger
              multiple
              accept=".csv,.zip"
              beforeUpload={(file) => {
                setUploadFiles((prev) => [...prev, file]);
                return false; // prevent auto upload
              }}
              onRemove={(file) => {
                setUploadFiles((prev) =>
                  prev.filter((f) => f.name !== file.name),
                );
              }}
              fileList={uploadFiles.map(
                (f) =>
                  ({
                    uid: f.name,
                    name: f.name,
                    size: f.size,
                    status: "done",
                  }) as UploadFile,
              )}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Arrastra archivos CSV o ZIP aquí
              </p>
              <p className="ant-upload-hint">
                Usa el ZIP que descargaste de esta misma página: los dos modos
                verifican su firma. Un CSV suelto (con el nombre de la tabla,
                ej. <code>usuarios.csv</code>) solo se acepta en ambientes sin
                firma configurada.
              </p>
            </Dragger>

            <Button
              type="primary"
              icon={<UploadOutlined />}
              loading={uploading}
              disabled={uploadFiles.length === 0}
              onClick={onUploadSubmit}
              danger={uploadMode === "restore"}
            >
              {uploadMode === "incremental"
                ? "Importar datos"
                : "Restaurar base de datos"}
            </Button>

            {/* Summary */}
            {summary && (
              <Card size="small" title="Resultado">
                {/* El resumen de filas por tabla siempre cuadraba consigo mismo,
                    así que un restore incompleto se leía como uno perfecto. Lo
                    que faltaba mostrar es todo esto. */}
                {extras && (
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%", marginBottom: 12 }}
                  >
                    {extras.sinMedios && (
                      <Alert
                        type="warning"
                        showIcon
                        message="Este backup no incluye los archivos del volumen"
                        description={
                          extras.advertenciaMedios ??
                          "No trae originales (fotos, videos, documentos), miniaturas ni versiones livianas. Los que no estén ya en el volumen quedarán como referencias rotas, visibles en /almacenamiento."
                        }
                      />
                    )}
                    {/* Con un backup sin medios, «0 archivos restaurados» es lo
                        esperado y ya lo explica el aviso de arriba. */}
                    {typeof extras.filesRestored === "number" && !extras.sinMedios && (
                      <Alert
                        type={
                          extras.filesFailed
                            ? "error"
                            : extras.filesRestored === 0
                              ? "warning"
                              : "success"
                        }
                        showIcon
                        message={`Archivos del Volume restaurados: ${extras.filesRestored}`}
                        description={
                          extras.filesFailed === -1
                            ? "No se pudo restaurar NINGÚN archivo (fotos, videos, documentos). Las filas quedaron en la base, pero los adjuntos van a aparecer rotos. No borres el ZIP."
                            : extras.filesFailed
                              ? `${extras.filesFailed} archivo(s) no se pudieron escribir. Revisa espacio en disco y permisos del Volume antes de dar el respaldo por restaurado.`
                              : extras.filesRestored === 0
                                ? "El ZIP no traía archivos del Volume (backup de la época «solo tablas»), o la carpeta venía vacía. Si esperabas fotos y documentos, este respaldo no los tiene."
                                : // Los mismos conceptos que la estimación de la descarga.
                                  (textoDesgloseRestaurado(
                                    extras.desgloseArchivos,
                                    extras.filesRestored,
                                  ) ?? undefined)
                        }
                      />
                    )}
                    {(extras.skipped?.length ?? 0) > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        message="Tablas del backup que ya no existen en la base"
                        description={`${extras.skipped!.join(", ")} — venían en el ZIP y no se restauraron porque la base actual no las tiene.`}
                      />
                    )}
                    {/* Cuentas que ya existían y quedaron con otro rol: el respaldo traía un rol distinto. Quien restaura tiene que enterarse. */}
                    {textoRolesCambiados(extras.usuariosCambiaronDeRol) !== null && (
                      <Alert
                        type="warning"
                        showIcon
                        data-testid="backup-roles-cambiados"
                        message={textoRolesCambiados(extras.usuariosCambiaronDeRol)}
                        description={
                          <>
                            El respaldo traía un rol distinto al que esas personas tenían hoy. Revisa que cada una tenga el rol que le
                            corresponde.
                            {textoSuperadministradores(extras.superadministradores) !== null && (
                              <>
                                {" "}
                                <span data-testid="backup-superadministradores">{textoSuperadministradores(extras.superadministradores)}.</span>
                              </>
                            )}
                          </>
                        }
                      />
                    )}
                    {(extras.noIncluidas?.length ?? 0) > 0 && (
                      <Alert
                        type="warning"
                        showIcon
                        message="Tablas vaciadas por ser posteriores al backup"
                        description={`${extras.noIncluidas!.join(", ")} — existían en la base y no venían en el ZIP, así que se vaciaron para no dejar datos mezclados.`}
                      />
                    )}
                  </Space>
                )}
                <Table
                  size="small"
                  pagination={false}
                  dataSource={Object.entries(summary).map(([table, info]) => ({
                    key: table,
                    table,
                    rows: (info as any).imported ?? (info as any).restored ?? 0,
                  }))}
                  columns={[
                    { title: "Tabla", dataIndex: "table", key: "table" },
                    {
                      title: "Filas procesadas",
                      dataIndex: "rows",
                      key: "rows",
                    },
                  ]}
                />
                <Button
                  type="link"
                  onClick={() => setSummary(null)}
                  style={{ marginTop: 8 }}
                >
                  Cerrar resultado
                </Button>
              </Card>
            )}
          </Space>
        </Card>

        {/* Restore confirmation modal */}
        <Modal
          title="Confirmar restauración completa"
          open={restoreModalVisible}
          onOk={confirmRestore}
          onCancel={() => setRestoreModalVisible(false)}
          okText="Confirmar"
          cancelText="Cancelar"
          okButtonProps={{ danger: true }}
        >
          <p>
            Esta acción eliminará todos los datos existentes en las tablas
            incluidas en los archivos y los reemplazará con los datos del
            backup.
          </p>
          <p>
            <Text strong type="danger">
              Esta operación no se puede deshacer.
            </Text>
          </p>
        </Modal>
      </main>
    </Layout>
  );
};

export default BackupPage;
